import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import type { TablesInsert, TablesUpdate } from "~/api/tables";
import { db } from "~/api/db";
import {
  invoiceRoundingMethod,
  projectBudgetType,
  requireText,
  resolveTimeEntryLinks,
  rootKey,
  workspaceKey,
  textOrNull,
  todayIsoDate,
  useWorkspaceMutation,
  type Client,
  type Project,
  type ProjectStatus,
  type UserRate,
} from "./shared";

export function useWorkspaceUserRates(workspaceId: string | undefined) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: workspaceKey("user-rates", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as UserRate[];
      const { data, error } = await db
        .from("user_rates")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("effective_from", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as UserRate[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "user-rates",
    async (input: Record<string, string>, targetWorkspaceId) => {
      if (!user) throw new Error("Sign in to create a rate preset.");
      const hourlyRate = Number(input.hourlyRate);
      if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) {
        throw new Error("Hourly rate must be greater than zero.");
      }
      const links = await resolveTimeEntryLinks(targetWorkspaceId, input);
      const { error } = await db.from("user_rates").insert({
        workspace_id: targetWorkspaceId,
        user_id: user.id,
        client_id: links.clientId,
        project_id: links.projectId,
        name: requireText(input.name, "Rate name"),
        description: textOrNull(input.description),
        effective_from: textOrNull(input.effectiveFrom) ?? todayIsoDate(),
        hourly_rate_cents: Math.round(hourlyRate * 100),
      } satisfies TablesInsert<"user_rates">);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "user-rates",
    async (
      input: { id: string; values: Record<string, string> },
      targetWorkspaceId,
    ) => {
      const hourlyRate = Number(input.values.hourlyRate);
      if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) {
        throw new Error("Hourly rate must be greater than zero.");
      }
      const links = await resolveTimeEntryLinks(
        targetWorkspaceId,
        input.values,
      );
      const { error } = await db
        .from("user_rates")
        .update({
          client_id: links.clientId,
          project_id: links.projectId,
          name: requireText(input.values.name, "Rate name"),
          description: textOrNull(input.values.description),
          effective_from:
            textOrNull(input.values.effectiveFrom) ?? todayIsoDate(),
          hourly_rate_cents: Math.round(hourlyRate * 100),
        } satisfies TablesUpdate<"user_rates">)
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const remove = useWorkspaceMutation(
    workspaceId,
    "user-rates",
    async (rateId: string, targetWorkspaceId) => {
      const { error } = await db
        .from("user_rates")
        .delete()
        .eq("workspace_id", targetWorkspaceId)
        .eq("id", rateId);
      if (error) throw error;
    },
  );

  return { ...query, create, rates: query.data ?? [], remove, update };
}

export function useWorkspaceClients(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: workspaceKey("clients", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Client[];
      const { data, error } = await db
        .from("clients")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Client[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "clients",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const { error } = await db.from("clients").insert({
        workspace_id: targetWorkspaceId,
        name: requireText(input.name, "Client name"),
        billing_address: textOrNull(input.billingAddress),
        contact_name: textOrNull(input.contactName),
        email: textOrNull(input.email),
        gst_number: textOrNull(input.gstNumber),
        invoice_rounding_override: input.invoiceRoundingOverride
          ? invoiceRoundingMethod(input.invoiceRoundingOverride)
          : null,
        notes: textOrNull(input.notes),
        phone: textOrNull(input.phone),
      } satisfies TablesInsert<"clients">);
      if (error) throw error;
    },
  );

  const setArchived = useWorkspaceMutation(
    workspaceId,
    "clients",
    async (input: { id: string; archived: boolean }) => {
      const { error } = await db
        .from("clients")
        .update({ archived: input.archived })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "clients",
    async (input: { id: string; values: Record<string, string> }) => {
      const { error } = await db
        .from("clients")
        .update({
          billing_address: textOrNull(input.values.billingAddress),
          contact_name: textOrNull(input.values.contactName),
          email: textOrNull(input.values.email),
          gst_number: textOrNull(input.values.gstNumber),
          invoice_rounding_override: input.values.invoiceRoundingOverride
            ? invoiceRoundingMethod(input.values.invoiceRoundingOverride)
            : null,
          name: requireText(input.values.name, "Client name"),
          notes: textOrNull(input.values.notes),
          phone: textOrNull(input.values.phone),
        } satisfies TablesUpdate<"clients">)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  return { ...query, create, setArchived, update };
}

export function useWorkspaceProjects(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: workspaceKey("projects", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Project[];
      const { data, error } = await db
        .from("projects")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Project[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "projects",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const budget = Number(input.budget);
      const budgetHours = Number(input.budgetHours);
      const hourlyRate = Number(input.hourlyRate);
      const { error } = await db.from("projects").insert({
        workspace_id: targetWorkspaceId,
        budget_type: projectBudgetType(input.budgetType),
        budget_cents:
          Number.isFinite(budget) && budget > 0
            ? Math.round(budget * 100)
            : null,
        budget_hours:
          Number.isFinite(budgetHours) && budgetHours > 0 ? budgetHours : null,
        client_id: textOrNull(input.clientId),
        name: requireText(input.name, "Project name"),
        description: textOrNull(input.description),
        ends_on: textOrNull(input.endsOn),
        status_id: textOrNull(input.statusId),
        hourly_rate_cents:
          Number.isFinite(hourlyRate) && hourlyRate > 0
            ? Math.round(hourlyRate * 100)
            : null,
        starts_on: textOrNull(input.startsOn),
      } satisfies TablesInsert<"projects">);
      if (error) throw error;
    },
  );

  const updateStatus = useWorkspaceMutation(
    workspaceId,
    "projects",
    async (input: { id: string; statusId: string | null }) => {
      const { error } = await db
        .from("projects")
        .update({ status_id: input.statusId })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const setArchived = useWorkspaceMutation(
    workspaceId,
    "projects",
    async (input: { id: string; archived: boolean }) => {
      const { error } = await db
        .from("projects")
        .update({ archived: input.archived })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "projects",
    async (input: { id: string; values: Record<string, string> }) => {
      const budget = Number(input.values.budget);
      const budgetHours = Number(input.values.budgetHours);
      const hourlyRate = Number(input.values.hourlyRate);
      const { error } = await db
        .from("projects")
        .update({
          budget_type: projectBudgetType(input.values.budgetType),
          budget_cents:
            Number.isFinite(budget) && budget > 0
              ? Math.round(budget * 100)
              : null,
          budget_hours:
            Number.isFinite(budgetHours) && budgetHours > 0
              ? budgetHours
              : null,
          client_id: textOrNull(input.values.clientId),
          description: textOrNull(input.values.description),
          ends_on: textOrNull(input.values.endsOn),
          hourly_rate_cents:
            Number.isFinite(hourlyRate) && hourlyRate > 0
              ? Math.round(hourlyRate * 100)
              : null,
          name: requireText(input.values.name, "Project name"),
          starts_on: textOrNull(input.values.startsOn),
          status_id: textOrNull(input.values.statusId),
          budget_alert_threshold_pct: (() => {
            const threshold = Number(input.values.budgetAlertThreshold);
            if (!Number.isFinite(threshold)) return 80;
            return Math.min(100, Math.max(1, threshold));
          })(),
        } satisfies TablesUpdate<"projects">)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const updateBudgetAlertThreshold = useWorkspaceMutation(
    workspaceId,
    "projects",
    async (input: { id: string; thresholdPct: number }) => {
      const threshold = Math.min(100, Math.max(1, input.thresholdPct));
      const { error } = await db
        .from("projects")
        .update({
          budget_alert_threshold_pct: threshold,
          // Reset alerts so a raised threshold can fire again later.
          budget_alert_threshold_sent_at: null,
          budget_alert_exhausted_sent_at: null,
        } satisfies TablesUpdate<"projects">)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  return {
    ...query,
    create,
    setArchived,
    update,
    updateBudgetAlertThreshold,
    updateStatus,
  };
}

export function useWorkspaceProjectStatuses(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("project-statuses", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as ProjectStatus[];
      const { data, error } = await db
        .from("project_statuses")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProjectStatus[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useMutation({
    mutationFn: async (input: {
      name: string;
      color: string;
      state: ProjectStatus["state"];
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const nextSortOrder =
        Math.max(-1, ...(query.data ?? []).map((status) => status.sort_order)) +
        1;
      const { error } = await db.from("project_statuses").insert({
        workspace_id: workspaceId,
        name: requireText(input.name, "Status name"),
        color: input.color,
        state: input.state,
        sort_order: nextSortOrder,
      } satisfies TablesInsert<"project_statuses">);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("project-statuses", workspaceId),
      });
    },
  });

  const update = useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      color: string;
      state: ProjectStatus["state"];
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("project_statuses")
        .update({
          name: requireText(input.name, "Status name"),
          color: input.color,
          state: input.state,
        } satisfies TablesUpdate<"project_statuses">)
        .eq("workspace_id", workspaceId)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("project-statuses", workspaceId),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (statusId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if ((query.data?.length ?? 0) <= 1) {
        throw new Error("A workspace needs at least one project status.");
      }
      const { count, error: countError } = await db
        .from("projects")
        .select("id", { count: "exact", head: true })
        .eq("workspace_id", workspaceId)
        .eq("status_id", statusId);
      if (countError) throw countError;
      if ((count ?? 0) > 0) {
        throw new Error("Move projects out of this status before removing it.");
      }
      const { error } = await db
        .from("project_statuses")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("id", statusId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("project-statuses", workspaceId),
      });
    },
  });

  return { ...query, create, update, remove };
}
